import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Send, Plus, Trash2, MessageSquare, Paperclip, X, FileText, Loader2 } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { ChatMessage, TypingIndicator } from "../components/ChatMessage";
import { SystemPromptModal } from "../components/SystemPromptModal";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import {
  getAgent,
  getSystemPrompt,
  setSystemPrompt,
  getActiveSessionId,
  setActiveSessionId,
} from "../lib/agents";

export default function ChatPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const agent = getAgent(agentId);

  const [prompt, setPrompt] = useState(() => getSystemPrompt(agentId));
  const [promptOpen, setPromptOpen] = useState(false);

  const [sessionId, setSessionIdState] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msConnected, setMsConnected] = useState(false);
  const [slowResponse, setSlowResponse] = useState(false);

  // Upload state — one attached file at a time (per user's product choice)
  const [pendingUpload, setPendingUpload] = useState(null); // { upload_id, filename, size, kind }
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const slowTimerRef = useRef(null);

  // Check Microsoft 365 connection status once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/microsoft/status");
        if (!cancelled) setMsConnected(Boolean(data?.connected));
      } catch (_) {
        if (!cancelled) setMsConnected(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Redirect if unknown or disabled agent
  useEffect(() => {
    if (!agent || agent.enabled === false) navigate("/");
  }, [agent, navigate]);

  // Load sessions list
  const refreshSessions = async () => {
    try {
      const { data } = await api.get(`/sessions`, { params: { agent_id: agentId } });
      setSessions(data || []);
      return data || [];
    } catch (e) {
      console.error("sessions fetch failed", e);
      return [];
    }
  };

  // Initial load: restore or create session
  useEffect(() => {
    if (!agent) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const list = await refreshSessions();
      if (cancelled) return;

      const stored = getActiveSessionId(agentId);
      const exists = list.find((s) => s.id === stored);

      if (exists) {
        setSessionIdState(stored);
      } else if (list.length > 0) {
        setSessionIdState(list[0].id);
        setActiveSessionId(agentId, list[0].id);
      } else {
        // Create first session
        const { data } = await api.post(`/sessions`, { agent_id: agentId });
        if (cancelled) return;
        setSessionIdState(data.id);
        setActiveSessionId(agentId, data.id);
        setSessions([data]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // Load messages whenever session changes
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const { data } = await api.get(`/sessions/${sessionId}/messages`);
        setMessages(data || []);
      } catch (e) {
        console.error("messages fetch failed", e);
        setMessages([]);
      }
    })();
  }, [sessionId]);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Refresh prompt when agent changes
  useEffect(() => {
    setPrompt(getSystemPrompt(agentId));
  }, [agentId]);

  const handleSend = async () => {
    const text = input.trim();
    if ((!text && !pendingUpload) || sending || !sessionId) return;
    // Always require some text alongside an upload — the agent needs to know
    // what to do with the file.
    if (!text) return;

    setInput("");
    const attachedForOptimistic = pendingUpload
      ? [
          {
            type: "user_upload",
            document_id: pendingUpload.upload_id,
            filename: pendingUpload.filename,
            size: pendingUpload.size,
            kind: pendingUpload.kind,
          },
        ]
      : [];
    const optimisticUser = {
      id: `tmp-${Date.now()}`,
      session_id: sessionId,
      role: "user",
      content: text,
      attachments: attachedForOptimistic,
      timestamp: new Date().toISOString(),
    };
    const sentUploadId = pendingUpload?.upload_id || null;
    setMessages((prev) => [...prev, optimisticUser]);
    setPendingUpload(null);
    setUploadError("");
    setSending(true);
    setSlowResponse(false);
    clearTimeout(slowTimerRef.current);
    slowTimerRef.current = setTimeout(() => {
      setSlowResponse(true);
    }, 4000);

    try {
      const { data } = await api.post(
        `/chat`,
        {
          session_id: sessionId,
          agent_id: agentId,
          system_prompt: prompt,
          message: text,
          upload_id: sentUploadId,
        },
        { timeout: 180000 } // 3 min hard cap; surfaces an error instead of spinning forever
      );
      setMessages((prev) => {
        const without = prev.filter((m) => m.id !== optimisticUser.id);
        return [...without, data.user_message, data.assistant_message];
      });
      refreshSessions();
    } catch (e) {
      console.error("chat failed", e);
      const isTimeout =
        e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      const serverDetail = e?.response?.data?.detail;
      const errMsg = isTimeout
        ? "The model took too long to respond. Please try again — if the issue persists, try a simpler prompt."
        : typeof serverDetail === "string" && serverDetail
        ? serverDetail
        : "Sorry, something went wrong reaching the model. Please try again.";
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          session_id: sessionId,
          role: "assistant",
          content: errMsg,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      clearTimeout(slowTimerRef.current);
      setSlowResponse(false);
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const ACCEPTED_TYPES = ".docx,.pdf,.xlsx,.csv,.txt,image/*";
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

  const handleAttachClick = () => {
    if (uploading || sending) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;

    setUploadError("");
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError("File is over 10 MB. Please attach a smaller file.");
      return;
    }
    const name = file.name.toLowerCase();
    const isImage = file.type.startsWith("image/");
    const allowed =
      isImage ||
      [".docx", ".pdf", ".xlsx", ".csv", ".txt"].some((ext) => name.endsWith(ext));
    if (!allowed) {
      setUploadError("Unsupported file type. Allowed: docx, pdf, xlsx, csv, txt, images.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setPendingUpload({
        upload_id: data.upload_id,
        filename: data.filename,
        size: data.size,
        kind: data.kind,
        truncated: data.truncated,
      });
    } catch (err) {
      console.error("upload failed", err);
      const detail = err?.response?.data?.detail;
      setUploadError(typeof detail === "string" ? detail : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      textareaRef.current?.focus();
    }
  };

  const handleRemovePendingUpload = () => {
    setPendingUpload(null);
    setUploadError("");
    textareaRef.current?.focus();
  };

  const handleNewSession = async () => {
    try {
      const { data } = await api.post(`/sessions`, { agent_id: agentId });
      setSessionIdState(data.id);
      setActiveSessionId(agentId, data.id);
      setMessages([]);
      refreshSessions();
    } catch (e) {
      console.error("create session failed", e);
    }
  };

  const handleDeleteSession = async (sid, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await api.delete(`/sessions/${sid}`);
      const list = await refreshSessions();
      if (sid === sessionId) {
        if (list.length > 0) {
          setSessionIdState(list[0].id);
          setActiveSessionId(agentId, list[0].id);
        } else {
          const { data } = await api.post(`/sessions`, { agent_id: agentId });
          setSessionIdState(data.id);
          setActiveSessionId(agentId, data.id);
          setSessions([data]);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error("delete failed", err);
    }
  };

  const handleSelectSession = (sid) => {
    setSessionIdState(sid);
    setActiveSessionId(agentId, sid);
  };

  const handleSavePrompt = (newPrompt) => {
    setSystemPrompt(agentId, newPrompt);
    setPrompt(newPrompt);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!agent) return null;
  const Icon = agent.icon;

  return (
    <div
      data-testid="chat-page"
      className="h-screen flex flex-col bg-brand text-brand-mint font-sans overflow-hidden relative"
    >
      {/* ambient mint glows */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute top-[-10%] right-[-15%] h-[500px] w-[700px] rounded-full bg-brand-mint/15 blur-[140px]" />
        <div className="absolute bottom-[-20%] left-[-10%] h-[480px] w-[640px] rounded-full bg-brand-mint/15 blur-[130px]" />
        <div className="absolute top-[40%] left-[50%] -translate-x-1/2 h-[260px] w-[420px] rounded-full bg-brand-mintBright/8 blur-[110px]" />
      </div>
      <div className="relative z-10 flex flex-col h-full">
      <Navbar />

      {/* Header */}
      <div className="border-b border-brand-mint/10 bg-brand/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/"
              data-testid="chat-back-btn"
              className="p-2 rounded-lg text-brand-mint/70 hover:text-brand-mint hover:bg-brand-deep/60 transition-colors shrink-0"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="h-10 w-10 rounded-xl bg-brand-deep/80 border border-brand-mint/20 flex items-center justify-center text-brand-mint shrink-0">
              <Icon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <h1
                data-testid="chat-agent-title"
                className="text-base md:text-lg font-semibold tracking-tight text-brand-mint truncate"
              >
                {agent.name}
              </h1>
              <p className="text-xs text-brand-mint/60 truncate">{agent.description}</p>
              {msConnected && (
                <div
                  data-testid="chat-capability-pills"
                  className="mt-1.5 flex flex-wrap items-center gap-1.5"
                >
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-deeper/60 border border-brand-mint/15 text-[10px] md:text-[11px] font-medium text-brand-mint/75">
                    <span aria-hidden="true">📧</span> Outlook
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-deeper/60 border border-brand-mint/15 text-[10px] md:text-[11px] font-medium text-brand-mint/75">
                    <span aria-hidden="true">📅</span> Calendar
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-deeper/60 border border-brand-mint/15 text-[10px] md:text-[11px] font-medium text-brand-mint/75">
                    <span aria-hidden="true">📁</span> OneDrive
                  </span>
                </div>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            data-testid="edit-system-prompt-btn"
            onClick={() => setPromptOpen(true)}
            className="border-brand-mint/25 bg-brand-deep/60 text-brand-mint hover:bg-brand-deep hover:text-brand-mintBright hover:border-brand-mint/50 shrink-0"
          >
            <Pencil className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Edit System Prompt</span>
            <span className="sm:hidden">Prompt</span>
          </Button>
        </div>
      </div>

      {/* Main split: sidebar + chat */}
      <div className="flex-1 flex min-h-0">
        {/* Sidebar: sessions */}
        <aside
          data-testid="sessions-sidebar"
          className="hidden md:flex w-64 lg:w-72 shrink-0 border-r border-brand-mint/10 bg-brand-deep/30 flex-col"
        >
          <div className="p-3">
            <Button
              onClick={handleNewSession}
              data-testid="new-session-btn"
              className="w-full bg-brand-mint hover:bg-brand-mintBright text-brand font-semibold justify-start"
            >
              <Plus className="h-4 w-4 mr-2" />
              New conversation
            </Button>
          </div>
          <div className="px-3 pb-2 text-xs uppercase tracking-widest text-brand-mint/55 font-medium">
            History
          </div>
          <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-1">
            {sessions.length === 0 && (
              <div className="px-3 py-2 text-sm text-brand-mint/55">No conversations yet.</div>
            )}
            {sessions.map((s) => (
              <button
                key={s.id}
                data-testid={`session-item-${s.id}`}
                onClick={() => handleSelectSession(s.id)}
                className={`group w-full text-left px-3 py-2.5 rounded-lg flex items-start gap-2 transition-colors ${
                  s.id === sessionId
                    ? "bg-brand-deep/80 border border-brand-mint/30 text-brand-mint"
                    : "border border-transparent text-brand-mint/80 hover:bg-brand-deep/60"
                }`}
              >
                <MessageSquare className="h-4 w-4 mt-0.5 shrink-0 text-brand-mint/55" />
                <span className="flex-1 text-sm truncate">{s.title || "New conversation"}</span>
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`session-delete-${s.id}`}
                  onClick={(e) => handleDeleteSession(s.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-brand-mint/55 hover:text-red-300 transition-opacity cursor-pointer"
                  aria-label="Delete conversation"
                >
                  <Trash2 className="h-4 w-4" />
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          <div
            ref={scrollRef}
            data-testid="chat-thread"
            className="flex-1 overflow-y-auto scroll-smooth"
          >
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-6">
              {loading ? (
                <div className="text-center text-brand-mint/55 text-sm py-12">Loading…</div>
              ) : messages.length === 0 ? (
                <div
                  data-testid="chat-empty-state"
                  className="flex flex-col items-center justify-center text-center py-16 md:py-24"
                >
                  <div className="h-14 w-14 rounded-2xl bg-brand-mint/15 border border-brand-mint/40 flex items-center justify-center text-brand-mint mb-5">
                    <Icon className="h-7 w-7" strokeWidth={1.75} />
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold text-brand-mint tracking-tight">
                    Start a conversation with{" "}
                    <span className="text-brand-mintBright">{agent.shortName}</span>
                  </h2>
                  <p className="mt-2 text-sm text-brand-mint/70 max-w-md">
                    {agent.description} Ask a question below to get started.
                  </p>
                </div>
              ) : (
                messages.map((m) => (
                  <ChatMessage
                    key={m.id}
                    role={m.role}
                    content={m.content}
                    attachments={m.attachments || []}
                  />
                ))
              )}
              {sending && (
                <div className="space-y-2">
                  <TypingIndicator />
                  {msConnected && slowResponse && (
                    <p
                      data-testid="chat-ms-loading-label"
                      className="pl-11 text-xs text-brand-mint/70 animate-in fade-in duration-300"
                    >
                      Accessing Microsoft 365…
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Input */}
          <div className="border-t border-brand-mint/10 bg-brand/80 backdrop-blur-xl">
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-4">
              {/* Pending upload chip */}
              {pendingUpload && (
                <div
                  data-testid="pending-upload-chip"
                  className="mb-2 inline-flex items-center gap-2 max-w-full pl-2 pr-1 py-1 rounded-lg bg-brand-deeper/70 border border-brand-mint/30"
                >
                  <FileText className="h-3.5 w-3.5 text-brand-mint shrink-0" />
                  <span className="text-xs text-brand-mint truncate max-w-[260px]">
                    {pendingUpload.filename}
                    {pendingUpload.truncated && (
                      <span className="ml-1 text-brand-mint/60">(truncated)</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemovePendingUpload}
                    data-testid="remove-pending-upload-btn"
                    className="h-5 w-5 rounded-md flex items-center justify-center text-brand-mint/70 hover:text-brand-mint hover:bg-brand-mint/10"
                    aria-label="Remove attached file"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {/* Upload error */}
              {uploadError && (
                <p
                  data-testid="upload-error"
                  className="mb-2 text-xs text-red-200"
                >
                  {uploadError}
                </p>
              )}
              <div className="flex items-end gap-2 p-2 rounded-2xl border border-brand-mint/20 bg-brand-deep/60 focus-within:border-brand-mint/50 focus-within:ring-2 focus-within:ring-brand-mint/20 transition-all">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  onChange={handleFileSelected}
                  className="hidden"
                  data-testid="chat-file-input"
                />
                <Button
                  type="button"
                  onClick={handleAttachClick}
                  disabled={uploading || sending || Boolean(pendingUpload)}
                  data-testid="chat-attach-btn"
                  title={
                    pendingUpload
                      ? "Remove the attached file to attach another"
                      : "Attach a file (docx, pdf, xlsx, csv, txt, image)"
                  }
                  className="bg-transparent hover:bg-brand-mint/10 text-brand-mint/70 hover:text-brand-mint h-10 w-10 p-0 shrink-0 rounded-xl disabled:opacity-40"
                  aria-label="Attach file"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4" />
                  )}
                </Button>
                <textarea
                  ref={textareaRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={1}
                  placeholder={`Message ${agent.shortName}…`}
                  className="flex-1 resize-none bg-transparent px-3 py-2.5 text-sm text-brand-mint placeholder:text-brand-mint/45 focus:outline-none max-h-40"
                  style={{ minHeight: "44px" }}
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || sending}
                  data-testid="chat-send-btn"
                  className="bg-brand-mint hover:bg-brand-mintBright text-brand h-10 w-10 p-0 shrink-0 rounded-xl disabled:opacity-40"
                  aria-label="Send message"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-2 text-center text-[11px] text-brand-mint/50">
                Claude Opus 4.5 · Press Enter to send, Shift+Enter for newline
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>

      <SystemPromptModal
        open={promptOpen}
        onOpenChange={setPromptOpen}
        agentName={agent.shortName}
        currentPrompt={prompt}
        defaultPrompt={agent.defaultPrompt}
        onSave={handleSavePrompt}
      />
    </div>
  );
}
